<template>
    <div class="mx-auto w-full max-w-sm lg:w-96">
        <div>
            <img class="h-10 w-auto" src="@/assets/logo.svg" alt="Gallera">
            <h2 class="mt-8 text-2xl/9 font-bold tracking-tight text-gray-900">{{ $t('auth_login.title') }}</h2>
            <p class="mt-2 text-sm/6 text-gray-500">
                {{ $t('auth_login.not_registered') }}
                <router-link to="/register" class="font-semibold text-royal-blue-600 hover:text-royal-blue-500">{{ $t('auth_login.try_free') }}</router-link>
            </p>
        </div>

        <div class="mt-10">
            <div>
                <form @submit.prevent="store" class="space-y-6">
                    <div>
                        <label for="email" class="block text-sm/6 font-medium text-gray-900">{{ $t('auth_login.email') }}</label>
                        <div class="mt-2 grid grid-cols-1">
                            <input
                                id="email"
                                type="email"
                                :class="{
                                    'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-royal-blue-600 sm:text-sm/6': true,
                                    'col-start-1 row-start-1 block w-full text-red-400 outline-red-400': hasError('email'),
                                }"
                                name="email"
                                v-model="form.email"
                                required
                                autocomplete="email"
                                autofocus>
                            <ExclamationCircleIcon v-if="hasError('email')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                        </div>
                        <p class="mt-2 text-sm text-red-600" id="email-error" v-if="hasError('email')">{{ getError('email') }}</p>
                    </div>

                    <div>
                        <div class="flex justify-between items-center">
                            <label for="password" class="block text-sm/6 font-medium text-gray-900">{{ $t('auth_login.password') }}</label>

                            <div class="text-sm/6">
                                <router-link to="/password/reset" class="font-semibold text-royal-blue-500 hover:text-royal-blue-600">{{ $t('auth_login.forgot_password') }}</router-link>
                            </div>
                        </div>
                        <div class="mt-2 grid grid-cols-1">
                            <input
                                id="password"
                                type="password"
                                :class="{
                                    'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-royal-blue-600 sm:text-sm/6': true,
                                    'col-start-1 row-start-1 block w-full text-red-400 outline-red-400': hasError('password'),
                                }"
                                name="password"
                                v-model="form.password"
                                required
                                autocomplete="current-password">
                            <ExclamationCircleIcon v-if="hasError('password')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                        </div>
                        <p class="mt-2 text-sm text-red-600" id="password-error" v-if="hasError('password')">{{ getError('password') }}</p>
                    </div>

                    <div>
                        <button type="submit" class="flex w-full justify-center rounded-md bg-royal-blue-500 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-royal-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-royal-blue-500 cursor-pointer">
                            {{ $t('auth_login.login_button') }}
                        </button>
                    </div>
                </form>
            </div>

            <div class="mt-8">
                <div class="relative">
                    <div class="absolute inset-0 flex items-center" aria-hidden="true">
                        <div class="w-full border-t border-gray-200"></div>
                    </div>
                    <div class="relative flex justify-center text-sm/6 font-medium">
                        <span class="bg-white px-6 text-gray-900">{{ $t('auth_login.or') }}</span>
                    </div>
                </div>

                <div class="mt-6 grid grid-cols-1 gap-4">
                    <a href="#" @click.prevent="authWith('google')" class="flex w-full items-center justify-center gap-3 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus-visible:ring-transparent">
                        <svg class="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z" fill="#EA4335" />
                            <path d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z" fill="#4285F4" />
                            <path d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z" fill="#FBBC05" />
                            <path d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z" fill="#34A853" />
                        </svg>
                        <span class="text-sm/6 font-semibold">{{ $t('auth_login.login_with_google') }}</span>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import {mapActions} from "vuex";
import {validationMethods} from "@/_helpers/validation";

import {ResponseError} from "@/types";
import {loginByProvider} from "@/data/requests/auth";
import {ExclamationCircleIcon} from "@heroicons/vue/16/solid";

export default defineComponent({
    name: 'AuthLogin',
    components: {ExclamationCircleIcon},

    data() {
        return {
            form: {
                email: null as string | null,
                password: null as string | null
            },

            errors: {} as { [key: string]: string[] }
            // errors: {"email": ["hello world"], "password": ["hello"]} as { [key: string]: string[] }
        };
    },

    watch: {
        'form.email': function() {
            delete this.errors['email'];
        },
        'form.password': function() {
            delete this.errors['password'];
        },
    },

    computed: {
        formDisabled: function(): boolean {
            return this.form.email === null || this.form.email === ''
                || this.form.password === null || this.form.password === ''
                || this.form.email.indexOf('@') === -1;
        }
    },

    mounted() {
        if(this.$route.name === 'login.social.callback') {
            this.loginBySocial({
                provider: this.$route.params.provider as string,
                query: this.$route.query
            }).then(() => {
                this.$router.push("/");
            })
                .catch(({type, data}: {type: ResponseError, data: any}) => {
                    if(type == ResponseError.Validation) {
                        this.errors = data.data;
                    }
                });
        }
    },

    methods: {
        ...mapActions({
            login: "user/login",
            loginBySocial: "user/loginBySocial",
        }),

        ...validationMethods(),

        authWith(driver: string) {
            loginByProvider(driver, (redirect_url) => {
                location.href = redirect_url;
            });
        },

        store() {
            this.login({
                email: this.form.email,
                password: this.form.password
            })
                .then(() => {
                    this.$router.push("/");
                })
                .catch(({type, data}: {type: ResponseError, data: any}) => {
                    if(type == ResponseError.Validation) {
                        this.errors = data.data;
                    }
                });
        },
    }
});
</script>

<style scoped>

</style>